(() => {
  if (!customElements.get('bash-studio-nav')) customElements.define('bash-studio-nav', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.departments = [...this.querySelectorAll('.bhs-department')];
      this.departments.forEach(department => {
        const tabs = [...department.querySelectorAll('.bhs-group-tab')];
        const list = department.querySelector('.bhs-group-list');
        // A tablist contains only tabs. Direct menu links remain ordinary links.
        if (tabs.length && !list.querySelector('.bhs-group-leaf')) {
          list.setAttribute('role','tablist');
          list.setAttribute('aria-orientation','vertical');
        }
        tabs.forEach((tab,index) => {
          if (list.getAttribute('role') === 'tablist') tab.setAttribute('role','tab');
          else tab.setAttribute('aria-expanded',String(index === 0));
          tab.addEventListener('click',() => this.selectTab(department,tab),options);
          tab.addEventListener('keydown',event => {
            const current = tabs.indexOf(tab);
            let target;
            if (event.key === 'ArrowDown') target = tabs[(current+1)%tabs.length];
            if (event.key === 'ArrowUp') target = tabs[(current-1+tabs.length)%tabs.length];
            if (event.key === 'Home') target = tabs[0];
            if (event.key === 'End') target = tabs[tabs.length-1];
            if (target) {event.preventDefault();this.selectTab(department,target);target.focus();}
            if (event.key === 'ArrowRight') {event.preventDefault();department.querySelector('.bhs-panel:not([hidden]) a')?.focus();}
          },options);
        });
        if (tabs[0]) this.selectTab(department,tabs[0]);
        department.addEventListener('toggle',() => {
          department.querySelector('summary').setAttribute('aria-expanded',String(department.open));
          if (department.open) {
            this.departments.filter(item => item !== department).forEach(item => item.open = false);
            this.closest('header')?.querySelector('predictive-search')?.close();
          }
        },options);
      });
      this.addEventListener('click',event => {
        if (event.target.closest('[data-bhs-close]')) this.close(event.target.closest('.bhs-department'),true);
      },options);
      this.addEventListener('keydown',event => {
        if (event.key === 'Escape') {
          const department = event.target.closest('.bhs-department[open]');
          if (department) {event.preventDefault();event.stopPropagation();this.close(department,true);}
        }
      },options);
      document.addEventListener('click',event => {
        if (!this.contains(event.target)) this.closeAll();
      },options);
      document.addEventListener('focusin',event => {
        if (!this.contains(event.target)) this.closeAll();
      },options);
      this.classList.add('is-ready');
    }
    selectTab(department,selected) {
      department.querySelectorAll('.bhs-group-tab').forEach(tab => {
        const active = tab === selected;
        if (tab.getAttribute('role') === 'tab') tab.setAttribute('aria-selected',String(active));
        tab.tabIndex = active ? 0 : -1;
        if (tab.hasAttribute('aria-expanded')) tab.setAttribute('aria-expanded',String(active));
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !active;
          panel.setAttribute('role',tab.getAttribute('role') === 'tab' ? 'tabpanel' : 'region');
        }
      });
      department.querySelector('.bhs-panels').scrollTop = 0;
    }
    close(department,focus=false) {
      if (!department) return;
      department.open = false;
      department.querySelector('summary').setAttribute('aria-expanded','false');
      if (focus) department.querySelector('summary').focus();
    }
    closeAll() {this.departments.forEach(department => this.close(department));}
    disconnectedCallback() {this.abort?.abort();}
  });

  if (!customElements.get('bash-studio-header')) customElements.define('bash-studio-header', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.section = this.closest('.section-header');
      this.always = ['always','reduce-logo-size'].includes(this.dataset.stickyType);
      this.previous = window.scrollY;
      this.observer = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height',this.section.offsetHeight+'px');
      });
      this.observer.observe(this.section);
      if (this.always) this.section.classList.add('shopify-section-header-sticky');
      window.addEventListener('scroll',() => {
        const top = window.scrollY;
        const occupied = this.section.classList.contains('menu-open') || this.querySelector('.bhs-department[open]') || this.querySelector('predictive-search[open]') || this.contains(document.activeElement);
        if (!this.always && !occupied) {
          const hidden = top > this.previous && top > this.section.offsetHeight + 80;
          this.section.classList.toggle('shopify-section-header-hidden',hidden);
          this.section.classList.toggle('shopify-section-header-sticky',top > this.section.offsetHeight);
        }
        this.section.classList.toggle('scrolled-past-header',top > this.section.offsetHeight);
        this.previous = top;
      },{passive:true,signal:this.abort.signal});
    }
    disconnectedCallback() {this.observer?.disconnect();this.abort?.abort();}
  });

  if (!customElements.get('bash-header-preview')) customElements.define('bash-header-preview', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.querySelectorAll('[data-bhs-width]').forEach(button => button.addEventListener('click',() => this.setWidth(button.dataset.bhsWidth),options));
      window.addEventListener('hashchange',() => this.applyHash(),options);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => this.applyHash(),{once:true,...options});
      else this.applyHash();
    }
    applyHash() {
      const mode = location.hash.match(/^#header-preview-(desktop|tablet|mobile)$/)?.[1] || 'desktop';
      this.setWidth(mode);
    }
    setWidth(mode) {
      document.querySelector('bash-studio-nav')?.closeAll();
      const drawer = document.querySelector('#Details-menu-drawer-container');
      if (drawer?.open) drawer.querySelector('summary').click();
      document.body.dataset.bhsPreview = mode;
      const main = document.querySelector('#MainContent');
      if (main) {
        if (mode === 'desktop') delete main.dataset.bhPreviewWidth;
        else main.dataset.bhPreviewWidth = mode;
      }
      this.querySelectorAll('[data-bhs-width]').forEach(button => button.setAttribute('aria-pressed',String(button.dataset.bhsWidth === mode)));
      const url = new URL(location.href);
      url.searchParams.set('preview_theme_id',this.dataset.themeId);
      url.searchParams.set('pb','0');
      url.hash = 'header-preview-'+mode;
      history.replaceState(null,'',url);
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }
    disconnectedCallback() {this.abort?.abort();delete document.body.dataset.bhsPreview;}
  });
})();
